import React from 'react';
import AccountDelegation from '@ux/account-delegation';
import { events, Customer, Employee } from '@ux/header-util';
import NavigationBottom from './nav-bottom';
import NavigationTop from './nav-top';
import ApplicationFooter from '@ux/application-footer';
import AbstractHeader, { LazyBrowserDeprecationBanner } from '@ux/abstract-header';
import HeaderComponent from './header';
import assign from 'object-assign';
import PropTypes from 'prop-types';
import Cart from '@px/header-cart';
import HubBanner from './hub-banner';
import { IntlProvider } from 'react-intl';

/**
 * Render and expose the Header as a functional Component.
 *
 * @param {Object} props Properties
 * @returns {ReactElement} Header.
 * @api public
 */
class Header extends AbstractHeader {
  constructor() {
    super(...arguments);
    this.state = {
      CartComponent: Cart, /* eslint-disable no-unused-vars */
      customWaffleLinks: null,
      helpUrl: null
    };
    this.updateCartComponent = NavigationTop.updateCartComponent.bind(this);
    this.updateNavigation = NavigationBottom.update.bind(this);
    this.updateTopNavigation = NavigationTop.update.bind(this);
    this.updateHelpUrl = NavigationTop.updateHelpUrl.bind(this);
    this.updateWaffleLinks = NavigationTop.updateWaffleLinks.bind(this);
  }

  componentDidMount() {
    super.componentDidMount();
    this.setExpVariantOnState({
      experimentId: 'dc_in_app_help_navigation',
      stateFieldName: 'inAppHelpNavLink'
    });
  }

  /**
   * Returns a valid Customer instance using the props associated
   * with this instance. If `internalMode` is `true` returns an `Employee`.
   *
   * @returns {User} Customer or Employee instance from @ux/header-util.
   */
  createCustomer() {
    const props = this.props;

    // Override customer if internal mode
    if (props.preset === 'internal-header') {
      return new Employee(props);
    }

    return new Customer(props);
  }

  /**
   * Prefer state over props when assigning to children
   *
   * @returns {Object} Child props
   * @api private
   */
  getChildProps() {
    const childProps = assign({}, this.props, this.state);
    return childProps;
  }

  render() {
    const childProps = this.getChildProps();
    const {
      market,
      messages,
      urls,
      preset,
      supportMatrix,
      whitelistedUserAgents,
      disableDeprecationBanner,
      blacklistedBrowsers,
      helpUrl
    } = childProps;

    return (
      <IntlProvider locale={ market } messages={ messages }>
        <div className={ this.isBrowser ? 'loaded' : null }>
          <LazyBrowserDeprecationBanner
            supportMatrix={ supportMatrix }
            whitelistedUserAgents={ whitelistedUserAgents }
            disableDeprecationBanner={ disableDeprecationBanner }
            blacklistedBrowsers={ blacklistedBrowsers }
            urls={ urls } />
          { preset === 'internal-header' ? null : (
            <AccountDelegation
              market={ market }
              messages={ messages }
              action={ urls.sso.exitDelegation.href }
              restoreCookie={ urls.sso.restoreCookie.href }
              customer={ this.customer }
            />
          ) }
          { childProps.privateLabelId === 1 && preset !== 'internal-header' ? (
            <HubBanner
              market={ market }
              messages={ messages }
              href={ urls.proHub.href }
            />
          ) : null }
          <HeaderComponent { ...childProps } />
        </div>
      </IntlProvider>
    );
  }
}

Header.propTypes = {
  market: PropTypes.string.isRequired,
  urls: PropTypes.object.isRequired,
  helpUrl: PropTypes.string,
  resellerUrls: PropTypes.object,
  privateLabelType: PropTypes.number,
  privateLabelId: PropTypes.number,
  preset: PropTypes.string,
  traffic: PropTypes.string,
  supportMatrix: PropTypes.object.isRequired,
  whitelistedUserAgents: PropTypes.array,
  disableDeprecationBanner: PropTypes.bool,
  blacklistedBrowsers: PropTypes.arrayOf(
    PropTypes.shape({
      browser: PropTypes.string,
      version: PropTypes.number
    })
  )
};

Header.defaultProps = {
  preset: 'application-header'
};

/**
 * Render and expose the Footer as a functional Component.
 *
 * @param {Object} props Properties
 * @returns {ReactElement} Footer.
 * @api public
 */
class Footer extends React.Component {
  componentDidMount() {
    events.emit('mount', 'footer', this);
    events.emit('mount:footer', this);
  }

  render() {
    return <ApplicationFooter { ...this.props } />;
  }
}

export { Header, Footer };
